import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, map, tap } from 'rxjs';
import {
  CodeValue,
  CodeCategory,
  CodeValueOption,
  CodeValueQueryParams,
  CreateCodeValueDto,
  UpdateCodeValueDto,
  sortCodeValues,
} from '../models/code-value';

// =============================================================================
// Code Value Service
// Manages lookup/reference values for dropdowns and labels.
// Reads from GET /api/code-values with in-memory per-category caching.
// =============================================================================
@Injectable({ providedIn: 'root' })
export class CodeValueService {
  private readonly http = inject(HttpClient);
  private readonly readUrl  = '/api/code-values';
  private readonly writeUrl = '/api/customizations';

  // In-memory cache for code values (loaded once, used everywhere)
  private readonly codeValuesCache = signal<CodeValue[]>([]);
  private readonly loadedCategories = signal<Set<CodeCategory>>(new Set());

  // =============================================================================
  // Read Operations
  // =============================================================================

  /**
   * Get code values by category.
   * Returns from cache on subsequent calls; fetches from API on first call.
   */
  getByCategory(category: CodeCategory, includeInactive = false): Observable<CodeValue[]> {
    const loaded = this.loadedCategories();
    if (loaded.has(category)) {
      const values = this.codeValuesCache()
        .filter(cv => cv.category === category)
        .filter(cv => includeInactive || cv.isActive);
      return of(sortCodeValues(values));
    }

    let params = new HttpParams().set('category', category);
    if (includeInactive) {
      params = params.set('includeInactive', 'true');
    }

    return this.http.get<any[]>(this.readUrl, { params }).pipe(
      map(values => values.map(v => {
        let metadata: Record<string, unknown> = {};
        try {
          const raw = v.metadataJson ?? v.metadata;
          if (raw && typeof raw === 'string') metadata = JSON.parse(raw);
          else if (raw && typeof raw === 'object') metadata = raw;
        } catch (e) { console.error('Failed to parse code value metadata', e); }
        return { ...v, metadata } as CodeValue;
      })),
      tap(values => {
        this.codeValuesCache.update(cached => {
          const filtered = cached.filter(cv => cv.category !== category);
          return [...filtered, ...values];
        });
        this.loadedCategories.update(set => {
          const newSet = new Set(set);
          newSet.add(category);
          return newSet;
        });
      }),
      map(values => sortCodeValues(
        values.filter(cv => includeInactive || cv.isActive)
      ))
    );
  }

  /**
   * Get code values as select options (simplified for dropdowns).
   */
  getOptionsForCategory(category: CodeCategory): Observable<CodeValueOption[]> {
    return this.getByCategory(category).pipe(
      map(values => values.map(cv => ({
        code: cv.code,
        name: cv.name,
        description: cv.description ?? undefined,
        metadata: cv.metadata,
      })))
    );
  }

  /**
   * Get a single code value from cache (sync lookup after category is loaded).
   */
  getCodeValue(category: CodeCategory, code: string): Observable<CodeValue | undefined> {
    // Try cache first
    const cached = this.codeValuesCache().find(
      cv => cv.category === category && cv.code === code
    );
    if (cached) {
      return of(cached);
    }

    // Fall back to loading the category
    return this.getByCategory(category, true).pipe(
      map(values => values.find(cv => cv.code === code))
    );
  }

  /**
   * Get display name for a code (synchronous, from cache)
   */
  getName(category: CodeCategory, code: string): string {
    const cached = this.codeValuesCache().find(
      cv => cv.category === category && cv.code === code
    );
    return cached?.name ?? code;
  }

  /**
   * Get all code values, optionally filtered by category.
   */
  getAll(params: CodeValueQueryParams = {}): Observable<CodeValue[]> {
    let httpParams = new HttpParams();
    if (params.category) {
      httpParams = httpParams.set('category', params.category);
    }
    if (params.includeInactive) {
      httpParams = httpParams.set('includeInactive', 'true');
    }

    return this.http.get<CodeValue[]>(this.readUrl, { params: httpParams }).pipe(
      map(values => sortCodeValues(
        params.includeInactive ? values : values.filter(cv => cv.isActive)
      ))
    );
  }

  // =============================================================================
  // Write Operations (Tenant-specific customization)
  // These call CustomizationsController which requires Customizations module access.
  // =============================================================================

  createCodeValue(dto: CreateCodeValueDto): Observable<CodeValue> {
    return this.http.post<CodeValue>(this.writeUrl, dto).pipe(
      tap(newValue => {
        this.codeValuesCache.update(values => [...values, newValue]);
      })
    );
  }

  updateCodeValue(dto: UpdateCodeValueDto): Observable<CodeValue> {
    // The backend uses a GUID id; dto should contain the id
    return this.http.put<CodeValue>(`${this.writeUrl}/${(dto as any).id}`, dto).pipe(
      tap(updated => {
        this.codeValuesCache.update(values =>
          values.map(cv =>
            cv.category === updated.category && cv.code === updated.code ? updated : cv
          )
        );
      })
    );
  }

  deleteCodeValue(category: CodeCategory, code: string): Observable<void> {
    const value = this.codeValuesCache().find(
      cv => cv.category === category && cv.code === code
    );

    if (!value) {
      throw new Error(`Code value not found: ${category}/${code}`);
    }

    return this.http.delete<void>(`${this.writeUrl}/${(value as any).codeValueId ?? (value as any).id}`).pipe(
      tap(() => {
        this.codeValuesCache.update(values =>
          values.filter(cv => !(cv.category === category && cv.code === code))
        );
      })
    );
  }
}
